/**
 * Wire codec boundary for the event framework.
 *
 * The replicated objects ([SignedCommitment], [LinkPreimage]) are transport-agnostic. [WireCodec] is the seam where a
 * concrete wire format plugs in; [LineWireCodec] is the default line-oriented, copy/paste-friendly encoding (consistent
 * with the existing WebRTC signaling style), and a binary or JSON codec could replace it without touching the store,
 * applier, or model.
 */

package com.oasisfeng.invaluable.event

import com.oasisfeng.invaluable.data.LinkProjection
import com.oasisfeng.invaluable.data.LinkProjectionScheme
import com.oasisfeng.invaluable.data.ProjectedLinkRecord
import com.oasisfeng.invaluable.identity.Codec
import kotlinx.io.bytestring.ByteString

/** Encode/decode a single [ReplicatedEvent] to and from a wire representation. */
interface WireCodec {
    fun encode(event: ReplicatedEvent): String
    fun decode(line: String): ReplicatedEvent?
}

/**
 * Default line codec, scoped to one projection [scheme].
 *
 * Because every projection in a store shares the same scheme, only payloads travel on the wire; the codec re-attaches
 * [scheme] on decode. Two line types mirror the two replicated objects:
 *
 * ```text
 * CMT|<headPayload>|<credentialPayload>|<signature>
 * LNK|<projectionPayload>|<parentPayload?>|<targetPayload?>
 * SEAL|<contentProjectionPayload>|<nonce>|<ciphertext>
 * ```
 *
 * All binary fields are base64url (no padding); an empty parent/target field denotes the kernel root.
 */
class LineWireCodec(private val scheme: LinkProjectionScheme) : WireCodec {

    override fun encode(event: ReplicatedEvent): String = when (event) {
        is SignedCommitment -> listOf(
            COMMITMENT_TAG,
            encodePayload(event.head),
            encodePayload(event.credential),
            encodeBytes(event.signature),
        ).joinToString(FIELD_SEPARATOR)
        is LinkPreimage -> listOf(
            PREIMAGE_TAG,
            encodePayload(event.record.projection),
            event.record.parent?.let(::encodePayload) ?: "",
            event.record.target?.let(::encodePayload) ?: "",
        ).joinToString(FIELD_SEPARATOR)
        is SealedContent -> listOf(
            SEALED_TAG,
            encodePayload(event.contentProjection),
            encodeBytes(event.nonce),
            encodeBytes(event.ciphertext),
        ).joinToString(FIELD_SEPARATOR)
    }

    override fun decode(line: String): ReplicatedEvent? {
        val parts = line.trim().split(FIELD_SEPARATOR)
        return when (parts.firstOrNull()) {
            COMMITMENT_TAG -> if (parts.size < 4) null else runCatching {
                SignedCommitment(decodeProjection(parts[1]), decodeProjection(parts[2]), decodeBytes(parts[3]))
            }.getOrNull()
            PREIMAGE_TAG -> if (parts.size < 4) null else runCatching {
                ProjectedLinkRecord(
                    projection = decodeProjection(parts[1]),
                    parent = parts[2].takeIf(String::isNotEmpty)?.let(::decodeProjection),
                    target = parts[3].takeIf(String::isNotEmpty)?.let(::decodeProjection),
                )
            }.getOrNull()?.let(::LinkPreimage)
            SEALED_TAG -> if (parts.size < 4) null else runCatching {
                SealedContent(decodeProjection(parts[1]), decodeBytes(parts[2]), decodeBytes(parts[3]))
            }.getOrNull()
            else -> null
        }
    }

    private fun encodePayload(projection: LinkProjection): String = encodeBytes(projection.payload)
    private fun decodeProjection(field: String): LinkProjection = LinkProjection(scheme, decodeBytes(field))
    private fun encodeBytes(bytes: ByteString): String = Codec.base64Url(bytes.toByteArray())
    private fun decodeBytes(field: String): ByteString = ByteString(*Codec.base64UrlDecode(field))

    companion object {
        private const val COMMITMENT_TAG = "CMT"
        private const val PREIMAGE_TAG = "LNK"
        private const val SEALED_TAG = "SEAL"
        private const val FIELD_SEPARATOR = "|"
    }
}
